import {Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output} from '@angular/core';

import {HttpClient, HttpEvent, HttpEventType, HttpHeaders, HttpRequest} from '@angular/common/http';
import {FileUpload} from 'primeng/fileupload';
import {ProgressBar} from 'primeng/progressbar';
import {Badge} from 'primeng/badge';
import {Button} from 'primeng/button';
import {Card} from 'primeng/card';
import {combineLatest, forkJoin, Observable, of, Subject, switchMap} from 'rxjs';
import {catchError, filter, finalize, first, map, takeUntil, tap} from 'rxjs/operators';
import {ToasterService} from "../../../../service/toaster.service";
import {TranslateModule, TranslatePipe, TranslateService} from "@ngx-translate/core";
import {FileUploader} from "ng2-file-upload";
import {StateService} from "../../../../service/state/state.service";
import {PrimeTemplate} from "primeng/api";

export interface DocumentUploadResponse {
  filename: string;
  size: number;
  uploadDate: Date;
  id: string;
}

export interface DocumentUploadError {
  filename: string;
  error: string;
}

@Component({
  selector: 'ibp-upload-area-wrapper',
  standalone: true,
  imports: [
    FileUpload,
    ProgressBar,
    Badge,
    Button,
    Card,
    TranslatePipe,
    TranslateModule,
    PrimeTemplate
  ],
  providers: [],
  templateUrl: './ibp-upload-area.component.html',
  styleUrl: './ibp-upload-area.component.scss'
})
export class DocumentUploadWrapperComponent implements OnInit, OnDestroy {
  private http = inject(HttpClient);

  private toasterService = inject(ToasterService);
  private translateService = inject(TranslateService);
  private state = inject(StateService);
  @Input() allowMultiple: boolean = true;
  @Input() maxFileSize: number = 10 * 1024 * 1024; // 10MB default
  @Input() uploadUrl: string = '';
  @Input() showUploadAction = true;
  @Input() showChooseAction = true;
  @Input() showClearAction = true;
  @Input() allowedMimeTypes: string[] = ['application/pdf', 'image/jpg', 'image/jpeg'];
  @Input() acceptedFileTypes: string = '.pdf,.jpg,.jpeg';
  @Input() withCredentials: boolean = false;
  @Input() authTokenHeader: string = 'auth_token';
  @Input() maxFilesNumber = 5;
  @Output() uploadSuccess = new EventEmitter<DocumentUploadResponse[]>();
  @Output() uploadError = new EventEmitter<DocumentUploadError[]>();
  @Output() filesSelected = new EventEmitter<File[]>();
  @Output() filesSelectedBase64 = new EventEmitter<string[]>();

  selectedFiles: File[] = [];
  uploading: boolean = false;
  uploadProgress: number = 0;
  uploadedDocuments: DocumentUploadResponse[] = [];
  public uploader: FileUploader;

  private destroy$ = new Subject<void>();



  ngOnInit() {
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get hasFiles(): boolean {
    return this.selectedFiles.length > 0;
  }

  onFileSelect(event: any) {
    const files = event.files || [];
    if((files?.length + this.selectedFiles?.length) > this.maxFilesNumber) {
      this.toasterService.pop('error',
        this.translateService.instant('IBP_IMAGE_UPLOAD.ERROR.UPLOAD_FAILED_TITLE'),
        this.translateService.instant('IBP_IMAGE_UPLOAD.ERROR.UPLOAD_EXCEEDED_MAX_NUMBER_OF_FILES_MESSAGE', {numberOfFiles: files.length}));
    } else {
      const validFiles: File[] = [];
      const invalidTypes: string[] = [];
      const invalidSize: string[] = [];

      for (const file of files) {
        if (!this.isValidFileType(file)) {
          invalidTypes.push(file.name);
        } else if(!this.isSizeValid(file)) {
          invalidSize.push(file.name);
        } else {
          validFiles.push(file);
        }
      }

      if (invalidTypes.length > 0) {
        this.toasterService.pop('warning',
          this.translateService.instant('IBP_IMAGE_UPLOAD.WARNING.INVALID_FILE_TYPE_TITLE'),
          this.translateService.instant('IBP_IMAGE_UPLOAD.WARNING.INVALID_FILE_TYPE_MESSAGE', {fileNames: invalidTypes.join(', ')}));
      }

      if (invalidSize.length > 0) {
        this.toasterService.pop('warning',
          this.translateService.instant('IBP_IMAGE_UPLOAD.WARNING.INVALID_FILE_TYPE_TITLE'),
          this.translateService.instant('IBP_IMAGE_UPLOAD.WARNING.INVALID_FILE_SIZE_MESSAGE', {fileNames: invalidSize.join(', ')}));
      }

      if (validFiles.length > 0) {
        this.selectedFiles = [...this.selectedFiles, ...validFiles];
        this.filesSelected.emit(this.selectedFiles);
        if (!this.uploadUrl) {
          this.emitFilesAsBase64();
        }
        this.toasterService.pop('info',
          this.translateService.instant('IBP_IMAGE_UPLOAD.INFO.FILES_SELECTED_TITLE'),
          this.translateService.instant('IBP_IMAGE_UPLOAD.INFO.FILES_SELECTED_MESSAGE', {numberOfFiles: validFiles.length}));
      }
    }
  }

  private isSizeValid(file: File) {
    return file.size <= this.maxFileSize;
  }

  private emitFilesAsBase64() {
    let base64ConvertFiles$ = [];
    this.selectedFiles.forEach(file => base64ConvertFiles$.push(
      this.convertFileToBase64(file)
        .pipe(
          first()
        )
      )
    )
    combineLatest([
      ...base64ConvertFiles$
    ])
      .pipe(
        first(),
        tap(value => {
          this.filesSelectedBase64.emit(value)
        })
      )
      .subscribe()

    if (this.selectedFiles.length === 0) {
      this.filesSelectedBase64.emit([]);
    }
  }

  onFileRemove(event: any) {
    this.selectedFiles = this.selectedFiles.filter(file => file !== event.file);
    if (!this.uploadUrl) {
      this.emitFilesAsBase64();
    }
  }

  onClear() {
    this.selectedFiles = [];
    this.uploadProgress = 0;
    if (!this.uploadUrl) {
      this.filesSelectedBase64.emit([]);
    }
  }

  removeFile(index: number) {
    this.selectedFiles.splice(index, 1);
    this.filesSelected.emit(this.selectedFiles);
    if (!this.uploadUrl) {
      this.emitFilesAsBase64();
    }
  }

  uploadFiles() {
    if (this.selectedFiles.length === 0) return;

    this.uploading = true;
    this.uploadProgress = 0;

    const uploadObservables = this.selectedFiles.map(file =>
      this.uploadSingleFile(file).pipe(
        catchError(error => of({
          error: true,
          filename: file.name,
          message: error.message || 'Upload failed'
        }))
      )
    );

    forkJoin(uploadObservables)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.uploading = false;
          this.uploadProgress = 100;
        })
      )
      .subscribe({
        next: (results) => {
          const successfulUploads: DocumentUploadResponse[] = [];
          const failedUploads: DocumentUploadError[] = [];

          results.forEach((result: any) => {
            if (result.error) {
              failedUploads.push({
                filename: result.filename,
                error: result.message
              });
            } else {
              successfulUploads.push(result);
            }
          });

          if (successfulUploads.length > 0) {
            this.uploadedDocuments = [...this.uploadedDocuments, ...successfulUploads];
            this.uploadSuccess.emit(successfulUploads);
            this.toasterService.pop('success',
              this.translateService.instant('IBP_IMAGE_UPLOAD.SUCCESS.UPLOAD_SUCCESSFUL_TITLE'),
              this.translateService.instant('IBP_IMAGE_UPLOAD.SUCCESS.UPLOAD_SUCCESSFUL_MESSAGE', {numberOfFiles: successfulUploads.length}));
          }

          if (failedUploads.length > 0) {
            this.uploadError.emit(failedUploads);

            this.toasterService.pop('error',
              this.translateService.instant('IBP_IMAGE_UPLOAD.ERROR.UPLOAD_FAILED_TITLE'),
              this.translateService.instant('IBP_IMAGE_UPLOAD.ERROR.UPLOAD_FAILED_MESSAGE', {numberOfFiles: failedUploads.length}));
          }

          this.selectedFiles = [];
        },
        error: (error) => {
          this.toasterService.pop('error',
            this.translateService.instant('IBP_IMAGE_UPLOAD.ERROR.UPLOAD_ERROR_TITLE'),
            this.translateService.instant('IBP_IMAGE_UPLOAD.ERROR.UPLOAD_ERROR_MESSAGE'));
        }
      });
  }

  private uploadSingleFile(file: File): Observable<DocumentUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('mimeType', file.type);
    formData.append('fileName', file.name);

    let headers = new HttpHeaders({
      'Accept': 'application/json'
    });

    return this.state.token$.pipe(
      first(),
      switchMap(token => {
        if (token) {
          headers = headers.set(this.authTokenHeader, token);
        }

        const request = new HttpRequest('POST', this.uploadUrl, formData, {
          reportProgress: true,
          headers: headers,
          withCredentials: this.withCredentials
        });

        return this.http.request<any>(request).pipe(
          map((event: HttpEvent<any>) => {
            if (event.type === HttpEventType.UploadProgress && event.total) {
              const progress = Math.round((100 * event.loaded) / event.total);
              this.uploadProgress = Math.max(this.uploadProgress, progress);
              return null;
            } else if (event.type === HttpEventType.Response) {
              return {
                filename: file.name,
                size: file.size,
                uploadDate: new Date(),
                id: event.body?.id,
                mimeType: file.type
              } as DocumentUploadResponse;
            }
            return null;
          }),
          filter(result => result !== null),
          map(result => result as DocumentUploadResponse),
          catchError(error => {
            throw new Error(`Upload failed: ${error.message || 'Unknown error'}`);
          })
        );
      })
    );
  }

  private isValidFileType(file: File): boolean {
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg'];
    const fileExtension = file.name.toLowerCase().split('.').pop();
    const allowedExtensions = ['pdf', 'jpg', 'jpeg'];

    return allowedTypes.includes(file.type) || allowedExtensions.includes(fileExtension || '');
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  formatDate(date: Date): string {
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  }

  private convertFileToBase64(file: File): Observable<string> {
    return new Observable<string>(observer => {
      if (!file) {
        observer.error(new Error('No file provided'));
        return;
      }

      const reader = new FileReader();

      reader.onload = (event: ProgressEvent<FileReader>) => {
        try {
          const result = event.target?.result as string;

          if (!result) {
            observer.error(new Error('Failed to read file'));
            return;
          }

          // Check if it's a data URL
          if (!result.startsWith('data:')) {
            observer.error(new Error('Invalid file format'));
            return;
          }

          const base64Content = result.split(',')[1];

          if (!base64Content) {
            observer.error(new Error('Failed to extract base64 content'));
            return;
          }

          observer.next(base64Content);
          observer.complete();
        } catch (error) {
          observer.error(error);
        }
      };

      reader.onerror = () => {
        observer.error(new Error(`Failed to read file: ${file.name}`));
      };

      reader.onabort = () => {
        observer.error(new Error(`File reading was aborted: ${file.name}`));
      };

      try {
        reader.readAsDataURL(file);
      } catch (error) {
        observer.error(error);
      }

      // Cleanup function - called when observable is unsubscribed
      return () => {
        if (reader.readyState === FileReader.LOADING) {
          reader.abort();
        }
      };
    });
  }
}
